import React, { useState } from "react";
import { ImagePreviewModal } from "./ImagePreviewModal.js";
import { publicMediaSrc } from "../utils/publicMediaUrl.js";

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
  gap: 12,
};

export interface CatalogListingPhotosPickerProps {
  /** URLs from `module_payloads.step_9_cataloging.image_urls` (field cataloging). */
  catalogUrls: string[];
  /** Subset of catalog URLs in catalog order; index 0 is the marketplace thumbnail. */
  selectedUrls: string[];
  onChange: (orderedSelected: string[]) => void;
  disabled?: boolean;
}

/**
 * Institution selects which DAA catalog photos are sent to the legacy marketplace
 * (`auction_info.image_urls` → `executePublicationGate` → POST create-with-images).
 */
export function CatalogListingPhotosPicker({
  catalogUrls,
  selectedUrls,
  onChange,
  disabled,
}: CatalogListingPhotosPickerProps): React.ReactElement {
  const [preview, setPreview] = useState<string | null>(null);
  const selectedSet = new Set(selectedUrls);

  function toggle(url: string): void {
    if (disabled) return;
    const next = new Set(selectedSet);
    if (next.has(url)) next.delete(url);
    else next.add(url);
    const ordered = catalogUrls.filter((u) => next.has(u));
    onChange(ordered);
  }

  return (
    <div style={{ marginTop: 4 }}>
      <ImagePreviewModal url={preview} onClose={() => setPreview(null)} />
      <ul
        style={{
          ...gridStyle,
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {catalogUrls.map((url, idx) => {
          const checked = selectedSet.has(url);
          const isPrimary = checked && selectedUrls[0] === url;
          const inputId = `catalog-photo-${idx}-${String(idx)}`;
          const displaySrc = publicMediaSrc(url);
          return (
            <li key={`${url}-${idx}`} style={{ position: "relative" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  borderRadius: "var(--radius-sm)",
                  border: checked ? "2px solid var(--brand)" : "1px solid var(--border)",
                  padding: 8,
                  background: checked ? "var(--brand-soft)" : "var(--panel)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setPreview(displaySrc)}
                  disabled={disabled}
                  style={{
                    position: "relative",
                    aspectRatio: "1",
                    borderRadius: "var(--radius-sm)",
                    overflow: "hidden",
                    background: "var(--slate-bg)",
                    border: "none",
                    padding: 0,
                    cursor: disabled ? "default" : "zoom-in",
                    width: "100%",
                  }}
                  aria-label={`Open catalog photo ${idx + 1} preview`}
                >
                  <img
                    src={displaySrc}
                    alt={`Catalog photo ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    loading="lazy"
                  />
                </button>
                <label
                  htmlFor={inputId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: disabled ? "default" : "pointer",
                  }}
                >
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(url)}
                    aria-label={`Include catalog photo ${idx + 1} in marketplace listing`}
                  />
                  <span style={{ fontSize: 12, color: "var(--ink)", fontWeight: 600 }}>
                    {isPrimary ? "Primary (thumbnail)" : "Include"}
                  </span>
                </label>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
